import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  IBieOnboardingValidationIssue,
} from './bieonboardingworkbench.models';

export interface IBieThresholdDecisionDialogProps {
  open: boolean;
  issue: IBieOnboardingValidationIssue | null;
  loading?: boolean;
  errorMessage?: string;
  reason: string;
  evidence: string;
  onReasonChange: (value: string) => void;
  onEvidenceChange: (value: string) => void;
  onOpenDriver: (issue: IBieOnboardingValidationIssue) => void;
  onOpenRecord: (issue: IBieOnboardingValidationIssue) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const read = (object: any, keys: string[]): any => {
  if (!object || typeof object !== 'object') return undefined;
  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null) return object[key];
  }
  for (const value of Object.values(object)) {
    const found = read(value, keys);
    if (found !== undefined && found !== null) return found;
  }
  return undefined;
};

const amount = (value: any): string => {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString(undefined, { maximumFractionDigits: 6 })
    : 'Not available';
};

export default function BieThresholdDecisionDialog(
  props: IBieThresholdDecisionDialogProps,
): React.ReactElement {
  const {
    open,
    issue,
    loading = false,
    errorMessage,
    reason,
    evidence,
    onReasonChange,
    onEvidenceChange,
    onOpenDriver,
    onOpenRecord,
    onClose,
    onSubmit,
  } = props;

  const details: any = issue?.vdd || {};
  const value = read(details, ['value', 'sourceValue', 'sourcevalue']) ?? issue?.src;
  const maximum = read(details, ['maximum', 'maximumValue', 'maximumvalue']);
  const driverCode = read(details,['driverCode', 'drivercode']);
  const driverId = read(details,['driverId', 'driverid']);
  const excess = Number(value) - Number(maximum);
  const percentage = Number(maximum)
    ? (excess / Number(maximum)) * 100
    : null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resolve Value Above Maximum</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Alert severity="warning">
            This value is outside the configured normal range. Correcting the
            Driver threshold or source workbook is preferred. A Governance
            Decision must be independently approved and remains auditable.
          </Alert>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Incoming Value</Typography>
              <Typography variant="h6">{amount(value)}</Typography>
            </Box>

            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Current Maximum</Typography>
              <Typography variant="h6">{amount(maximum)}</Typography>
            </Box>

            <Box sx={{ flex: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Excess</Typography>
              <Typography variant="h6">{amount(excess)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {percentage === null ? 'Percentage unavailable' : `${percentage.toFixed(2)}%`}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body2">
            Driver: <strong>{driverCode || driverId || 'Unknown'}</strong>
          </Typography>

          <Divider />

          <Typography variant="subtitle2">Preferred remediation</Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={() => issue && onOpenDriver(issue)} disabled={loading}>
              Review Driver Threshold
            </Button>
            <Button variant="outlined" onClick={() => issue && onOpenRecord(issue)} disabled={loading}>
              Correct Source Record
            </Button>
          </Stack>

          <Divider />

          <Typography variant="subtitle2">Request Governance Decision</Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Exceptional event justification"
            value={reason}
            required
            disabled={loading}
            onChange={(event) => onReasonChange(event.target.value)}
            helperText="Explain why this is a genuine exceptional event rather than a stale threshold or source-data error."
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Supporting evidence reference"
            value={evidence}
            disabled={loading}
            onChange={(event) => onEvidenceChange(event.target.value)}
            helperText="Reference a document, approval, market event, audit finding or other supporting evidence."
          />

          <Alert severity="info">
            The requester cannot approve or reject their own request. No bulk
            exception approval is available.
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading || !reason.trim()}
        >
          Request Governance Decision
        </Button>
      </DialogActions>
    </Dialog>
  );
}
